'use client'

import { confirm } from '@repo/ui/Confirm'
import { Portal } from '@mui/base/Portal'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useNavigate } from 'react-router-dom'
import { useNewClientStore } from '@repo/store/useNewClientStore'
import { useNewMatterStore } from '@repo/store/useNewMatterStore'
import { useNotificationStore } from '@repo/store/useNotificationStore'
import { useState, useEffect } from 'react'
import { useTheme } from '@mui/material/styles'
import { useUrlStore } from '@repo/store/useUrlStore'
import { useWizardStore } from '@repo/store/useWizardStore'
import { WizardHeader } from '@repo/ui/WizardHeader'
import { WizardSidebar } from '@repo/ui/WizardSidebar'
import { WizardStepContents } from '@repo/ui/WizardStepContents'
import MUIBackdrop from '@mui/material/Backdrop'
import MUIPaper from '@mui/material/Paper'
import MUIStack from '@mui/material/Stack'
import useMediaQuery from '@mui/material/useMediaQuery'
import WizardConfigs from '@repo/app-assets/WizardConfigs'

export const WizardBase = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [wizardId, setWizardId] = useState()
  const [currentStageId, setCurrentStageId] = useState()
  const [currentStage, setCurrentStage] = useState()
  const [currentStepId, setCurrentStepId] = useState()
  const [currentStep, setCurrentStep] = useState()
  const [uniqueStepId, setUniqueStepId] = useState(null)
  const { queryParams, replaceQueryParams } = useUrlStore()
  const { setPageScrollable } = useGlobalSettingsStore()
  const theme = useTheme()
  const wideMode = useMediaQuery(theme.breakpoints.up('desktop'))
  const {
    setCurrentStepStateId,
    setWizardConfig,
    wizardConfig,
    wizardState,
    reset: resetWizardStore,
    wizardIsComplete,
    wizardStepsByIndex,
    wizardStagesByIndex,
    wizardStagesById,
    wizardStepsById
  } = useWizardStore()
  const { resetWizardMatterState } = useNewMatterStore()
  const { resetWizardClientState } = useNewClientStore()
  const { isNotificationWizard, setIsNotificationWizard, unlockNotification, notificationId } = useNotificationStore()
  const navigate = useNavigate()

  useEffect(() => {
    const isOpen = Boolean(queryParams.wizard)
    setIsOpen(isOpen)
  }, [queryParams, setPageScrollable])

  useEffect(() => {
    setPageScrollable(!isOpen)
  }, [isOpen, setPageScrollable])

  useEffect(() => {
    const { stage, step, wizard } = queryParams
    setCurrentStageId(stage)
    setCurrentStepId(step)
    setWizardId(wizard)

    const wizardConfig = WizardConfigs[wizard]
    setWizardConfig({ wizardId: wizard, wizardConfig })
  }, [queryParams, setWizardConfig])

  useEffect(() => {
    if (!wizardConfig) return
    const currentStage = wizardConfig.find((stage) => stage.stageId === currentStageId)
    setCurrentStage(currentStage)
  }, [currentStageId, wizardConfig])

  useEffect(() => {
    const currentStep = currentStage?.steps.find((step) => step.stepId === currentStepId)
    setCurrentStep(currentStep)
    setUniqueStepId(currentStep?.uniqueId)
  }, [currentStepId, currentStage])

  useEffect(() => {
    setCurrentStepStateId(`${wizardId}---${currentStageId}---${currentStepId}`)
  }, [currentStepId, currentStageId, wizardId, setCurrentStepStateId])

  const updateURL = ({ stageId, stepId }) => {
    if (!window.location.hash) return

    // get the current url query params
    const pathSearch = decodeURIComponent(window.location.hash).split('?')[1]
    const queryParams = pathSearch.split('&').reduce((acc, item) => {
      const [key, value] = item.split('=')
      acc[key] = value
      return acc
    }, {})

    const { stage, step, wizard } = queryParams || {}
    setWizardId(wizard)

    if (!stage) return

    const url = window.location.href
    let updatedURL

    if (stageId) {
      updatedURL = url.replace(`stage=${stage}`, `stage=${stageId}`)
      setCurrentStageId(stageId)
    }

    if (!updatedURL) updatedURL = url

    if (stepId) {
      if (step) {
        updatedURL = updatedURL.replace(`step=${step}`, `step=${stepId}`)
      } else {
        updatedURL = `${updatedURL}&step=${stepId}`
      }

      setCurrentStepId(stepId)
    } else {
      updatedURL = updatedURL.replace(`&step=${step}`, ``)
      setCurrentStepId(null)
    }

    history.pushState({}, 'Update wizard url', updatedURL)
  }

  const closeWizard = async () => {
    if (!wizardIsComplete) {
      const confirmed = await confirm('Are you sure you want to close this window? You will lose all unsaved changes.')
      if (!confirmed) return
    }

    if (isNotificationWizard) {
      await resetWizardClientState()
      await resetWizardMatterState()
      unlockNotification(notificationId)
      setIsNotificationWizard(false)
    }

    resetWizardStore()
    // reset the store for each stage
    wizardConfig?.forEach((stage) => {
      if (stage.resetData) stage.resetData()
    })
    const wizardQueries = window.location.href.split('?')[1]
    const updatedURL = window.location.href.replace(wizardQueries, '').replace('?', '')
    history.pushState({}, 'Close wizard', updatedURL)
    setIsOpen(false)
  }

  const handleStageButtonClick = (stageId) => {
    const targetStage = wizardStagesById[stageId]
    const prevStage = targetStage.index ? wizardStagesByIndex[targetStage.index - 1] : null
    const targetStageIndex = targetStage.index
    const currentStageIndex = currentStage.index

    if (targetStageIndex > currentStageIndex) {
      // prevent users from going to later stages if the previous stage is not complete

      const stepIdsInPrevStage = prevStage?.steps?.map((step) => step.uniqueId)
      const prevStageIsComplete = stepIdsInPrevStage?.every((stepId) => {
        const state = wizardState[stepId]
        if (!state || !state.status) return false
        return state.status === 'completed' || state.status === 'skipped'
      })

      if (!prevStageIsComplete) return
    }

    const stepId = wizardConfig.find((stage) => stage.stageId === stageId)?.steps[0]?.stepId
    updateURL({ stageId, stepId })
  }

  const handleStepButtonClick = (uniqueId) => {
    const targetStep = wizardStepsById[uniqueId]
    const targetStepStatus = wizardState[uniqueId]?.status

    const prevStep = targetStep.index ? wizardStepsByIndex[targetStep.index - 1] : null
    const prevStepStatus = prevStep ? wizardState[prevStep.uniqueId]?.status : null

    if (
      !prevStep ||
      targetStepStatus === 'completed' ||
      targetStepStatus === 'skipped' ||
      prevStepStatus === 'completed' ||
      prevStepStatus === 'skipped'
    )
      updateURL({ stepId: targetStep.stepId })
  }

  const handlePrevNext = (delta) => {
    const targetStep = wizardStepsByIndex[currentStep.index + delta]
    const { stageId, stepId } = targetStep
    updateURL({ stageId, stepId })
  }

  const changeWizard = (wizardId) => {
    if (!wizardId) return
    const newHash = replaceQueryParams({ param: 'wizard', value: wizardId })
    if (typeof newHash === 'string') navigate(newHash)
  }

  return (
    <>
      <MUIPaper
        data-testid="WizardBase"
        elevation={20}
        sx={{
          width: 'calc(100% - 40px)',
          height: 'calc(100% - 40px)',
          maxWidth: '1440px',
          maxHeight: '1140px',
          position: 'fixed',
          margin: '20px',
          top: isOpen ? 'calc(50% - 20px)' : 'calc(50% - 50px)',
          left: 'calc(50% - 20px)',
          transform: `translate(-50%, -50%)`,
          zIndex: 999,
          transition: 'all 550ms',
          opacity: isOpen ? 1 : 0,
          visibility: isOpen ? 'visible' : 'hidden',
          pointerEvents: isOpen ? 'auto' : 'none'
        }}
      >
        {isOpen ? (
          <MUIStack
            direction="row"
            sx={{
              width: '100%',
              height: '100%'
            }}
          >
            <WizardSidebar
              wideMode={wideMode}
              title={currentStage?.stageLabel}
              wizardConfig={wizardConfig}
              handleStageButtonClick={handleStageButtonClick}
              handleStepButtonClick={handleStepButtonClick}
              currentStageId={currentStageId}
              currentStepId={currentStepId}
              wizardId={wizardId}
            />
            <MUIStack
              direction="column"
              alignItems="stretch"
              sx={{ flex: 1, maxWidth: wideMode ? 'calc(100% - 300px)' : 'calc(100% - 270px)' }}
            >
              <WizardHeader
                onClose={closeWizard}
                title={currentStep?.stepLabel}
                description={currentStep?.stepDescription}
                wideMode={wideMode}
              />

              <MUIStack
                direction="column"
                data-testid="WizardStepContentsContainer"
                sx={{
                  width: '100%',
                  height: 'calc(100% - 72px)',
                  padding: wideMode ? '60px 60px 116px' : '40px 40px 116px',
                  overflow: 'auto',
                  ...currentStep?.contentSx
                }}
              >
                <WizardStepContents
                  currentStep={currentStep}
                  wizardId={wizardId}
                  wideMode={wideMode}
                  handlePrevNext={handlePrevNext}
                  changeWizard={changeWizard}
                  uniqueStepId={uniqueStepId}
                />
              </MUIStack>
            </MUIStack>
          </MUIStack>
        ) : null}
      </MUIPaper>

      <Portal>
        <MUIBackdrop open={isOpen} sx={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 900 }} />
      </Portal>
    </>
  )
}